import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Clock, Award, Briefcase, Video, MessageCircle, Phone, Mail, MapPin, CheckCircle, Calendar, Stethoscope, Home, ShoppingBag, Pill, TestTube, Heart, ChevronRight } from 'lucide-react';
import api from '../../api/axios';
import Header from '../common/Header';
import Footer from '../common/Footer';
import Skeleton from 'react-loading-skeleton';

const API_BASE_URL = 'https://www.homecureexpress.com';

export default function DoctorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Suggestion states
  const [suggestedMedicines, setSuggestedMedicines] = useState([]);
  const [suggestedServices, setSuggestedServices] = useState([]);
  const [suggestedTests, setSuggestedTests] = useState([]);
  const [suggestedPackages, setSuggestedPackages] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  useEffect(() => {
    fetchDoctorProfile();
    fetchSuggestions();
  }, [id]);

  const fetchDoctorProfile = async () => {
    try {
      setLoading(true);
      // Use correct API endpoint: /api/doctors/<doctor_id>/
      const response = await api.get(`/doctors/${id}/`);

      // Handle response format: { "success": true, "data": {...} }
      if (response.data.success && response.data.data) {
        setDoctor(response.data.data);
      } else if (response.data) {
        setDoctor(response.data);
      } else {
        setError('Failed to load doctor profile');
      }
    } catch (error) {
      console.error('Error fetching doctor profile:', error);
      setError(error.response?.data?.error || 'Failed to load doctor profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      setLoadingSuggestions(true);

      // Fetch all three suggestions in parallel
      const [medicinesRes, servicesRes, testsRes] = await Promise.all([
        api.get(`/suggestions/medicines-for-doctor/${id}/`).catch(() => null),
        api.get(`/suggestions/services-for-doctor/${id}/`).catch(() => null),
        api.get(`/suggestions/tests-for-doctor/${id}/`).catch(() => null)
      ]);

      // Set medicines
      if (medicinesRes?.data?.success) {
        setSuggestedMedicines(medicinesRes.data.suggested_medicines || []);
      }

      // Set services
      if (servicesRes?.data?.success) {
        setSuggestedServices(servicesRes.data.suggested_services || []);
      }

      // Set tests and packages
      if (testsRes?.data?.success) {
        setSuggestedTests(testsRes.data.suggested_tests || []);
        setSuggestedPackages(testsRes.data.suggested_packages || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    return `${API_BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-5xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex flex-col md:flex-row gap-8">
                <Skeleton circle width={200} height={200} />
                <div className="flex-1">
                  <Skeleton height={40} width={300} className="mb-4" />
                  <Skeleton height={24} width={250} className="mb-4" />
                  <Skeleton count={3} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !doctor) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-5xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Doctor Not Found</h2>
              <p className="text-gray-600 mb-6">{error || 'The doctor profile you are looking for does not exist.'}</p>
              <button
                onClick={() => navigate('/online-consultation')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Back to Doctors List
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/online-consultation')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-6"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Doctors List</span>
          </button>

          {/* Profile Card */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-600 to-green-600 p-8 text-white">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Profile Image */}
                <div className="relative">
                  {doctor.profile_image ? (
                    <img
                      src={getImageUrl(doctor.profile_image)}
                      alt={doctor.name}
                      className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-white/20 flex items-center justify-center border-4 border-white shadow-lg">
                      <Award className="w-16 h-16 md:w-20 md:h-20" />
                    </div>
                  )}
                  {doctor.is_available_now && (
                    <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 border-4 border-white rounded-full"></div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">Dr. {doctor.name}</h1>
                  <p className="text-xl text-blue-100 mb-3">{doctor.specialization}</p>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4">
                    {doctor.experience_years > 0 && (
                      <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full">
                        <Briefcase className="w-4 h-4" />
                        <span className="text-sm">{doctor.experience_years}+ Years Experience</span>
                      </div>
                    )}

                    {doctor.average_rating > 0 && (
                      <div className="flex items-center space-x-2 bg-white/20 px-3 py-1 rounded-full">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm">{parseFloat(doctor.average_rating).toFixed(1)} ({doctor.total_reviews} reviews)</span>
                      </div>
                    )}

                    {doctor.is_available_now && (
                      <div className="flex items-center space-x-2 bg-green-500 px-3 py-1 rounded-full">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium">Available Now</span>
                      </div>
                    )}
                  </div>

                  {/* Consultation Fee */}
                  <div className="inline-block bg-white text-gray-900 px-6 py-3 rounded-lg shadow-lg">
                    <p className="text-sm text-gray-600 mb-2">Consultation Fees</p>
                    <div className="space-y-1">
                      {doctor.is_available_chat && (
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-xs text-gray-600">Chat:</span>
                          <span className="text-sm font-bold text-green-600">
                            {parseFloat(doctor.price_chat) > 0 ? `₹${parseFloat(doctor.price_chat).toFixed(0)}` : 'Free'}
                          </span>
                        </div>
                      )}
                      {doctor.is_available_video && (
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-xs text-gray-600">Video:</span>
                          <span className="text-sm font-bold text-green-600">₹{parseFloat(doctor.price_video).toFixed(0)}</span>
                        </div>
                      )}
                      {doctor.is_available_hospital_normal && (
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-xs text-gray-600">Hospital (Normal):</span>
                          <span className="text-sm font-bold text-green-600">₹{parseFloat(doctor.price_hospital_normal).toFixed(0)}</span>
                        </div>
                      )}
                      {doctor.is_available_hospital_priority && (
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-xs text-gray-600">Hospital (Priority):</span>
                          <span className="text-sm font-bold text-green-600">₹{parseFloat(doctor.price_hospital_priority).toFixed(0)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* About */}
                  {doctor.bio && (
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <Award className="w-6 h-6 mr-2 text-blue-600" />
                        About
                      </h2>
                      <p className="text-gray-700 leading-relaxed">{doctor.bio}</p>
                    </div>
                  )}

                  {/* Qualifications */}
                  {doctor.qualifications && (
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <Award className="w-6 h-6 mr-2 text-blue-600" />
                        Qualifications
                      </h2>
                      <p className="text-gray-700 leading-relaxed">{doctor.qualifications}</p>
                    </div>
                  )}

                  {/* Services */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Services Offered</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {doctor.video_call_enabled && (
                        <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <Video className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">Video Consultation</span>
                        </div>
                      )}
                      {doctor.is_available_online && (
                        <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <MessageCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <span className="text-gray-700">Chat Consultation</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reviews */}
                  {doctor.recent_reviews && doctor.recent_reviews.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Reviews</h2>
                      <div className="space-y-4">
                        {doctor.recent_reviews.map((review, index) => (
                          <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-semibold text-gray-900">{review.patient_name}</p>
                              <div className="flex items-center space-x-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="text-gray-700 text-sm">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Contact Info */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
                    <div className="space-y-3">
                      {doctor.phone && (
                        <div className="flex items-start space-x-3">
                          <Phone className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Phone</p>
                            <p className="text-gray-900 font-medium">{doctor.phone}</p>
                          </div>
                        </div>
                      )}
                      {doctor.email && (
                        <div className="flex items-start space-x-3">
                          <Mail className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Email</p>
                            <p className="text-gray-900 font-medium break-all">{doctor.email}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    {doctor.video_call_enabled && (
                      <button
                        onClick={() => navigate('/online-consultation')}
                        className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
                      >
                        <Video className="w-5 h-5" />
                        <span>Book Video Consultation</span>
                      </button>
                    )}

                    {doctor.is_available_online && (
                      <button
                        onClick={() => navigate('/online-consultation')}
                        className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>Start Chat Consultation</span>
                      </button>
                    )}
                  </div>

                  {/* Availability Info */}
                  {doctor.availability_patterns && doctor.availability_patterns.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Clock className="w-5 h-5 mr-2 text-blue-600" />
                        Availability
                      </h3>
                      <div className="space-y-2">
                        {doctor.availability_patterns.map((pattern, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span>{pattern}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recommended Medicines */}
              {suggestedMedicines.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                      <Pill className="w-6 h-6 mr-2 text-blue-600" />
                      Recommended Medicines
                    </h2>
                    <button
                      onClick={() => navigate('/medicines')}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {suggestedMedicines.slice(0, 4).map((medicine) => (
                      <div
                        key={medicine.id}
                        onClick={() => navigate(`/medicine/${medicine.id}`)}
                        className="bg-white rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-blue-400"
                      >
                        <div className="relative mb-3">
                          {medicine.image ? (
                            <img
                              src={getImageUrl(medicine.image)}
                              alt={medicine.name}
                              className="w-full h-32 object-cover rounded-lg"
                              onError={(e) => {
                                if (!e.target.dataset.errored) {
                                  e.target.dataset.errored = 'true';
                                  e.target.src = 'https://via.placeholder.com/150x150/e3f2fd/1976d2?text=Medicine';
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-32 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-4xl">💊</span>
                            </div>
                          )}
                          {medicine.discount_percentage > 0 && (
                            <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                              {Math.round(medicine.discount_percentage)}% OFF
                            </div>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2">{medicine.name}</h3>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-lg font-bold text-green-600">₹{medicine.offer_price}</span>
                            {medicine.mrp && medicine.offer_price < medicine.mrp && (
                              <span className="text-xs text-gray-500 line-through ml-2">₹{medicine.mrp}</span>
                            )}
                          </div>
                        </div>
                        {medicine.requires_prescription && (
                          <div className="text-xs text-orange-600 font-medium">℞ Prescription Required</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Paramedical Services */}
              {suggestedServices.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                      <Heart className="w-6 h-6 mr-2 text-purple-600" />
                      Recommended Home Care Services
                    </h2>
                    <button
                      onClick={() => navigate('/paramedical')}
                      className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {suggestedServices.slice(0, 3).map((service) => (
                      <div
                        key={service.id}
                        onClick={() => navigate(`/paramedical/${service.id}`)}
                        className="bg-white rounded-lg p-5 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-purple-400"
                      >
                        <div className="relative mb-3">
                          {service.image ? (
                            <img
                              src={getImageUrl(service.image)}
                              alt={service.title}
                              className="w-full h-40 object-cover rounded-lg"
                              onError={(e) => {
                                if (!e.target.dataset.errored) {
                                  e.target.dataset.errored = 'true';
                                  e.target.src = 'https://via.placeholder.com/300x200/f3e5f5/9c27b0?text=Service';
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-40 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg flex items-center justify-center">
                              <span className="text-5xl">🏥</span>
                            </div>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">{service.title}</h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{service.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-purple-600">₹{service.price}</span>
                          <span className="text-xs text-gray-500">{service.duration_days} day(s)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Medical Tests & Packages */}
              {(suggestedTests.length > 0 || suggestedPackages.length > 0) && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                      <TestTube className="w-6 h-6 mr-2 text-pink-600" />
                      Recommended Medical Tests
                    </h2>
                    <button
                      onClick={() => navigate('/medical-tests')}
                      className="text-pink-600 hover:text-pink-700 font-medium text-sm flex items-center"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>

                  {/* Test Packages */}
                  {suggestedPackages.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Test Packages</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                        {suggestedPackages.slice(0, 2).map((pkg) => (
                          <div
                            key={pkg.id}
                            onClick={() => navigate(`/test-package/${pkg.id}`)}
                            className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-5 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-pink-200 hover:border-pink-400"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <h3 className="font-bold text-gray-900 text-lg flex-1">{pkg.name}</h3>
                              {pkg.discount_percentage > 0 && (
                                <span className="bg-pink-500 text-white text-xs font-bold px-2 py-1 rounded">
                                  {pkg.discount_percentage}% OFF
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{pkg.description}</p>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-gray-600">{pkg.test_count || pkg.tests_count} tests included</span>
                            </div>
                            <div className="flex items-center justify-between pt-3 border-t border-pink-200">
                              <div>
                                <span className="text-xl font-bold text-pink-600">₹{pkg.offer_price || pkg.effective_price}</span>
                                {pkg.total_price && pkg.offer_price < pkg.total_price && (
                                  <span className="text-sm text-gray-500 line-through ml-2">₹{pkg.total_price}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Individual Tests */}
                  {suggestedTests.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Individual Tests</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {suggestedTests.slice(0, 4).map((test) => (
                          <div
                            key={test.id}
                            className="bg-white rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-pink-400"
                          >
                            <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{test.title}</h3>
                            <p className="text-xs text-gray-600 mb-3 line-clamp-2">{test.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-lg font-bold text-pink-600">₹{test.offer_price || test.effective_price}</span>
                              {test.price && test.offer_price < test.price && (
                                <span className="text-xs text-gray-500 line-through">₹{test.price}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Suggestions Section - All Services */}
              <div className="mt-8 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 border-2 border-blue-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Explore Our Other Services</h2>
                <p className="text-gray-600 text-center mb-6">
                  Complete healthcare solutions for your family
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* In-Person Appointment */}
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-blue-400 cursor-pointer group"
                    onClick={() => navigate('/doctor-list')}
                  >
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                      <Calendar className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-center mb-2 text-lg">Book Appointment</h3>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      Schedule in-person consultation at our clinic
                    </p>
                    <div className="flex items-center justify-center text-blue-600 group-hover:text-blue-700 font-medium text-sm">
                      <span>Book Now</span>
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Video Consultation */}
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-green-400 cursor-pointer group"
                    onClick={() => navigate('/online-consultation')}
                  >
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-600 transition-colors">
                      <Video className="w-7 h-7 text-green-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-center mb-2 text-lg">Video Consultation</h3>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      Connect with doctors via video call from home
                    </p>
                    <div className="flex items-center justify-center text-green-600 group-hover:text-green-700 font-medium text-sm">
                      <span>Start Now</span>
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Paramedical Services */}
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-purple-400 cursor-pointer group"
                    onClick={() => navigate('/paramedical')}
                  >
                    <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-600 transition-colors">
                      <Home className="w-7 h-7 text-purple-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-center mb-2 text-lg">Home Care Services</h3>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      Nursing, physiotherapy & medical care at home
                    </p>
                    <div className="flex items-center justify-center text-purple-600 group-hover:text-purple-700 font-medium text-sm">
                      <span>Explore Services</span>
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Order Medicines */}
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-orange-400 cursor-pointer group"
                    onClick={() => navigate('/medicines')}
                  >
                    <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-600 transition-colors">
                      <ShoppingBag className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-center mb-2 text-lg">Order Medicines</h3>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      Get medicines delivered to your doorstep
                    </p>
                    <div className="flex items-center justify-center text-orange-600 group-hover:text-orange-700 font-medium text-sm">
                      <span>Order Now</span>
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="mt-6 bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Stethoscope className="w-6 h-6 text-blue-600 mt-1" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <strong className="text-gray-900">Complete Healthcare at Your Fingertips:</strong> From consultations
                        to home care services and medicine delivery, we provide comprehensive healthcare solutions for your entire family.
                        Available 24/7 with professional medical support.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
